//カードテンプレート編集画面-デザインタブ
import React, { useState } from 'react'
import styled from 'styled-components'
import { TextField, Grid, Box, FormControlLabel, Checkbox, IconButton, MenuItem } from '@material-ui/core';
import MessageOutlinedIcon from '@material-ui/icons/MessageOutlined';
import { nanoid } from 'nanoid';

import { CardTemplateDb } from '../redux/cardTemplateDbSlice';
import { CardTemplateAttributeDb } from '../redux/cardTemplateAttributeDbSlice';
import { CardTemplateShapeDb } from '../redux/cardTemplateShapeDbSlice';
import { AttributeKinds, defaultFont, defaultFrame } from '../redux/cardAttributeModelIndex';
import useWindowSize from '@rehooks/window-size'
import useElementDimensions from '../hooks/useElementDimensions';
import { deepCopy, FullSizeImageAddType } from '../general';
import { initial } from './index';
import ItemMenu from '../components/ItemMenu'
import SelectLanguage from '../components/SelectLanguage'
import RenderCard from '../components/RenderCard'
import ItemDesignArea from '../components/ItemDesignArea';
import { EditTemplateAttributeDbHashType, EditTemplateShapeDbHashType } from '../components/EditTemplateDialog';
import ImageBrowserDialog from '../components/ImageBrowserDialog';
import ChangeAttributeOrderDialog from '../components/ChangeAttributeOrderDialog';
import DrawToolDialog from '../components/DrawToolDialog';
import ChangeCardTemplateNotesDialog from '../components/ChangeCardTemplateNotesDialog';
import SetCardCustomSizeDialog from '../components/SetCardCustomSizeDialog';
import ColorPalette from '../components/ColorPalette';

const ItemField = styled(TextField)`
  width: 99%;
`

const isAttribute = (obj: any): obj is CardTemplateAttributeDb => {
  return typeof obj?.cardAttributeModel === 'string';
}
const isShape = (obj: any): obj is CardTemplateShapeDb => {
  return typeof obj?.kind === 'string';
}

//デザインタブ-テンプレート名入力
type TemplateNameAreaProps = { 
  template: CardTemplateDb;
  setTemplate: (template: CardTemplateDb) => void;
}
const TemplateNameArea: React.FC<TemplateNameAreaProps> = ({template, setTemplate}) => {
  return (
    <Box ml={1} mt={2} mb={1}>
      <ItemField variant="outlined" 
        defaultValue={template.name} 
        label='カードテンプレート名'
        placeholder='お気に入りテンプレート'
        onChange={(ev) => {setTemplate({...template, name: ev.target.value})}}
      />
    </Box>
  );
}

//デザインタブ-カード表示
type CardDesignAreaProps = {
  card: CardTemplateDb;
  attributes: EditTemplateAttributeDbHashType;
  shapes: EditTemplateShapeDbHashType;
  changeItem: (item: CardTemplateAttributeDb | CardTemplateShapeDb | null) => void;
  selectingId: string | undefined;
  setSelectingId: (id: string | undefined) => void;
  width?: number;
  height?: number;
  changeTab: () => void;
}
const CardDesignArea: React.FC<CardDesignAreaProps> = ({ card, attributes, shapes, changeItem, selectingId, setSelectingId, width, height, changeTab }) => {
  const windowSize = useWindowSize();
  const workWidth = width ? width - 16 : (windowSize.innerWidth - (64 * 2)) * 0.6;
  const workHeight = height ? height : (windowSize.innerHeight - (64 * 2)) * 0.5;
  const widthScale = workWidth / card.defaultWidth;
  const heightScale = workHeight / card.defaultHeight;
  const scaleValue = Math.min(widthScale, heightScale);

  return (
    <Box ml={1}>
      <RenderCard card={card} attributes={attributes} shapes={shapes}
                  selectingId={selectingId} setSelectingId={setSelectingId}
                  scaleValue={scaleValue} workHeight={workHeight}
                  changeItem={changeItem}
                  changeTab={changeTab}
      />
    </Box>
  );
}


//カードテンプレート編集画面-デザインタブ
type DesignTemplateProps = {
  templateId: string;
  template: CardTemplateDb
  setTemplate: (template: CardTemplateDb) => void
  attributes: EditTemplateAttributeDbHashType;
  setAttributes: (items: EditTemplateAttributeDbHashType) => void;
  shapes: EditTemplateShapeDbHashType;
  setShapes: (shapes: EditTemplateShapeDbHashType) => void;
  splitUpdated: boolean;
}
const DesignTemplate: React.FC<DesignTemplateProps> = ({ templateId, template, setTemplate, attributes, setAttributes, shapes, setShapes, splitUpdated }) => {
  const windowSize = useWindowSize()
  const workHeight = windowSize.innerHeight - (64 * 2) - 130
  const attributeIds = Object.keys(attributes).filter(id => attributes[id]);
  const shapeIds = Object.keys(shapes).filter(id => shapes[id]);
  const ids = attributeIds.concat(shapeIds);
  const [selectingId, setSelectingId] = useState<string | undefined>(undefined)// 選択された項目のid
  const [languageIndex, setLanguageIndex] = useState(0) //言語設定
  type ImageBrowserActionType = { show: boolean; type?: 'attribute' | 'shape' }
  const [imageBrowserAction, setImageBrowserAction] = useState<ImageBrowserActionType>({ show: false });
  type DrawToolActionType = { show: boolean; type?: 'attribute' | 'shape' }
  const [drawToolAction, setDrawToolAction] = useState<DrawToolActionType>({ show: false });
  const [changeAttributeOrderAction, setChangeAttributeOrderAction] = useState(false);
  const [changeCardTemplateNotesAction, setChangeCardTemplateNotesAction] = useState(false);
  const [itemDesignTab, setItemDesignTab] = useState(0);
  const [sizeCustomMenuOpen, setSizeCustomMenuOpen] = useState<boolean>(false);
  const cardDesignAreaId = `card-design-area-${templateId}`;
  const { width, height } = useElementDimensions(cardDesignAreaId, splitUpdated);

  const heightInVertical = 270;
  type CardSizeType = 'horizontal' | 'vertical' | 'custom'
  const getCardSizeSelectValue = (): CardSizeType => {
    if (template.defaultWidth === initial.cardWidth && template.defaultHeight === initial.cardHeight) return 'horizontal';
    else if (template.defaultWidth === initial.cardWidth && template.defaultHeight === 270) return 'vertical';
    else return 'custom';
  }
  const changeCardSize = (size: unknown) => {
    const isProps = (value: unknown): value is { width: number; height: number; } => {
      if (!value) return false;
      return Boolean(value) && typeof value === 'object' &&
        'width' in value && typeof value.width === 'number' &&
        'height' in value && typeof value.height === 'number';
    }
    const newTemplate = deepCopy(template);
    if (size === 'horizontal') {
      newTemplate.defaultWidth = initial.cardWidth;
      newTemplate.defaultHeight = initial.cardHeight;
    } else if (size === 'vertical') {
      newTemplate.defaultWidth = initial.cardWidth;
      newTemplate.defaultHeight = heightInVertical;
    } else if (isProps(size)) {
      newTemplate.defaultWidth = size.width;
      newTemplate.defaultHeight = size.height;
    }
    setTemplate(newTemplate);
  }
  const changeGroup = (value: boolean) => {
    const newTemplate = deepCopy(template);
    newTemplate.isGroup = value;
    setTemplate(newTemplate);
  }
  const changeFrameDisplay = (display: boolean) => {
    const newTemplate = deepCopy(template);
    newTemplate.frame.display = display;
    setTemplate(newTemplate);
  }
  const changeFrameColor = (color: string) => {
    const newTemplate = deepCopy(template);
    newTemplate.frame.color = color;
    setTemplate(newTemplate); 
  }
  const changeBackgroundDisplay = (display: boolean) => {
    const newTemplate = deepCopy(template);
    newTemplate.background.display = display;
    setTemplate(newTemplate);
  }
  const changeBackgroundColor = (color: string) => {
    const newTemplate = deepCopy(template);
    newTemplate.background.color = color;
    setTemplate(newTemplate); 
  }
  const changeShadowDisplay = (display: boolean) => {
    const newTemplate = deepCopy(template);
    newTemplate.shadow = display;
    setTemplate(newTemplate);
  }
  const addNewAttribute = (kind: AttributeKinds, image?: FullSizeImageAddType) => {
    const newAttributes = deepCopy(attributes);
    const newId = 'newAttribute:' + nanoid();
    const newAttribute: EditTemplateAttributeDbHashType = {};
    const commonProps = {
      cardTemplateId: templateId, languageId: 'ja',
      frame: defaultFrame, order: attributeIds.length,
      x: 0, y: 20, z: ids.length, width: 100, height: 30, rotation: 0,
      description: {display: false, content: ''},
      background: {display: false, color: '#ffffff'}
    } as const;
    //選択された入力項目の初期値をカードテンプレートに追加する
    if (kind === 'single') {
      newAttribute[newId] = {
        ...commonProps,
        cardAttributeModel: 'single', name: '単一行項目', hint: '入力してください',
        font: defaultFont, defaultContent: '',
      };
      Object.assign(newAttributes, newAttribute);
    } else if (kind === 'multi') {
      newAttribute[newId] = {
        ...commonProps,
        cardAttributeModel: 'multi', name: '複数行項目', hint: '改行できます',
        height: 60,
        font: initial.multiAttributeFont, defaultContent: '',
        isMarkdown: false
      };
      Object.assign(newAttributes, newAttribute);
    } else if (kind === 'table') {
      newAttribute[newId] = {
        ...commonProps,
        cardAttributeModel: 'table', name: '表項目', hint: 'ダイアログが開きます',
        height: 60,
        font: defaultFont, columnNames: {},
      };
      Object.assign(newAttributes, newAttribute);
    } else if (kind === 'image' && image) {
      newAttribute[newId] = {
        ...commonProps,
        cardAttributeModel: 'image', name: '画像項目', hint: '画像を表示します',
        width: 60, height: 60,
        defaultImage: {
          imageId: image.id,
          originWidth: image.width,
          originHeight: image.height,
          trimXFrom: 0,
          trimYFrom: 0,
          trimXTo: image.width,
          trimYTo: image.height
        },
      };
      Object.assign(newAttributes, newAttribute);
    } else if (kind === 'nondisplay') {
      newAttribute[newId] = {
        ...commonProps,
        cardAttributeModel: 'nondisplay', name: '非表示項目', hint: 'カード上には表示されません',
        defaultContent: ''
      };
      Object.assign(newAttributes, newAttribute);
    } else return;

    setAttributes(newAttributes);
    setSelectingId(newId);
  }
  const addNewShape = (kind: string, image?: FullSizeImageAddType) => {
    const newShapes = deepCopy(shapes);
    const newId = 'newShape:' + nanoid();
    const newShape: EditTemplateShapeDbHashType = {};
    const commonProps = {
      cardTemplateId: templateId,
      x: 0, y: 20, z: ids.length, width: 100, height: 30, rotation: 0,
      frame: defaultFrame,
      background: { display: false, color: '#ffffff' }
    } as const;
    if (kind === 'text') {
      newShape[newId] = {
        ...commonProps,
        kind: 'text', name: 'テキスト',
        text: 'テキストを入力してください', font: initial.textShapeFont
      };
      Object.assign(newShapes, newShape);
    } else if (kind === 'circle') {
      newShape[newId] = {
        ...commonProps,
        kind: 'circle', name: '円',
        width: 50, height: 50,
        stroke: {display: true, color: '#000000', opacity: 1, width: 1},
        fill: {display: false, color: '#000000', opacity: 1}
      };
      Object.assign(newShapes, newShape);
    } else if (kind === 'rectangle') {
      newShape[newId] = {
        ...commonProps,
        kind: 'rectangle', name: '四角',
        width: 100, height: 30,
        stroke: {display: true, color: '#000000', opacity: 1, width: 1},
        fill: {display: false, color: '#000000', opacity: 1}
      };
      Object.assign(newShapes, newShape);
    } else if (kind === 'balloon') {
      newShape[newId] = {
        ...commonProps,
        kind: 'balloon', name: 'ふきだし',
        width: 100, height: 60,
        stroke: {display: true, color: '#000000', opacity: 1, width: 1},
        fill: {display: false, color: '#000000', opacity: 1}
      };
      Object.assign(newShapes, newShape);
    } else if (kind === 'reverseBalloon') {
      newShape[newId] = {
        ...commonProps,
        kind: 'balloon', name: 'ふきだし',
        width: -100, height: 60,
        stroke: {display: true, color: '#000000', opacity: 1, width: 1},
        fill: {display: false, color: '#000000', opacity: 1}
      };
      Object.assign(newShapes, newShape);
    } else if (kind === 'image' && image) {
      newShape[newId] = {
        ...commonProps,
        kind: 'image', name: '固定画像',
        width: 60, height: 60,
        image: {
          imageId: image.id,
          originWidth: image.width,
          originHeight: image.height,
          trimXFrom: 0,
          trimYFrom: 0,
          trimXTo: image.width,
          trimYTo: image.height
        }
      };
      Object.assign(newShapes, newShape);
    }

    setShapes(newShapes);
    setSelectingId(newId);
  }
  const changeItem = (item: CardTemplateAttributeDb | CardTemplateShapeDb | null, id?: string) => {
    if (selectingId === undefined) return;
    const targetId = id ? id : selectingId;
    const newAttributes = deepCopy(attributes);
    const newShapes = deepCopy(shapes);
    if (isAttribute(item)) {
      newAttributes[targetId] = item;
      setAttributes(newAttributes);
    } else if (isShape(item)) {
      newShapes[targetId] = item;
      setShapes(newShapes);
    }
  }
  const changeTab = () => {
    setItemDesignTab(1);
  }

  return(
    <>
      <Box>
        <Grid container spacing={0}>
          <Grid item xs={8}>
            <Grid container spacing={0}>
              <Grid item xs={1}>
                <Box style={{height: workHeight, overflow: 'auto'}}>
                  <ItemMenu cardKind='template'
                            onAddAttribute={addNewAttribute} onAddShape={addNewShape}
                            onImageBrowser={(type) => setImageBrowserAction({ show: true, type: type })}
                            onDrawTool={(type) => setDrawToolAction({ show: true, type: type })} />
                </Box>
              </Grid>
              <Grid item xs={11}>
                <Grid container alignItems="center">
                  <Grid item xs={10}>
                    <TemplateNameArea 
                      template={template}
                      setTemplate={setTemplate}
                    />
                  </Grid>
                  <Grid item xs={2} style={{ marginTop: 8 }}>
                    <IconButton onClick={() => setChangeCardTemplateNotesAction(true)}><MessageOutlinedIcon /></IconButton>
                  </Grid>
                  <Grid item xs={12} id={cardDesignAreaId} style={{marginBottom: '24px'}}>
                    <CardDesignArea card={template}
                      attributes={attributes} shapes={shapes}
                      changeItem={changeItem}
                      selectingId={selectingId}
                      setSelectingId={setSelectingId}
                      width={width} height={height}
                      changeTab={changeTab}
                    />
                  </Grid>
                  <Grid item xs={12}>
                   <Grid container alignItems="center">
                      <TextField
                        select variant="outlined"
                        value={getCardSizeSelectValue()}
                        onChange={ev => changeCardSize(ev.target.value)}
                        label="サイズ"
                      >
                        <MenuItem value="horizontal">横（{initial.cardWidth} × {initial.cardHeight}）</MenuItem>
                        <MenuItem value="vertical">縦（{initial.cardWidth} × {heightInVertical}）</MenuItem>
                        { getCardSizeSelectValue() === 'custom' &&
                          <MenuItem value="custom">{template.defaultWidth} × {template.defaultHeight}</MenuItem>
                        }
                        <MenuItem onClick={() => setSizeCustomMenuOpen(true)}>カスタム...</MenuItem>
                      </TextField>
                      <FormControlLabel label="グルーピングカードにする" style={{ marginLeft: 8 }}
                        control={
                          <Checkbox checked={template.isGroup} color="primary"
                            onChange={(ev) => changeGroup(ev.currentTarget.checked)} />
                        } />
                    </Grid>
                  </Grid>
                  <Grid container item xs={12} style={{overflow: 'hidden'}}>
                    <Grid item alignItems="center" xs={12} md={4}>
                      <Grid container alignItems="center">
                        <FormControlLabel label="枠線表示"
                          control={
                            <Checkbox checked={template.frame.display} color="primary"
                              onChange={(ev) => changeFrameDisplay(ev.currentTarget.checked)} />
                          } />
                        <ColorPalette
                          currentValue={template.frame.color}
                          onChange={changeFrameColor}
                          disabled={!template.frame.display}
                        />
                      </Grid>
                    </Grid>
                    <Grid item alignItems="center" xs={12} md={4}>
                      <Grid container alignItems="center">
                        <FormControlLabel label="背景表示"
                          control={
                            <Checkbox checked={template.background.display} color="primary"
                              onChange={(ev) => changeBackgroundDisplay(ev.currentTarget.checked)} />
                          } />
                          <ColorPalette
                            currentValue={template.background.color}
                            onChange={changeBackgroundColor}
                            disabled={!template.background.display}
                          />
                        </Grid>
                    </Grid>
                    <Grid item alignItems="center" xs={12} md={4}>
                      <FormControlLabel label="影あり"
                        control={
                          <Checkbox checked={template.shadow} color="primary"
                            onChange={(ev) => changeShadowDisplay(ev.currentTarget.checked)} />
                      } />
                    </Grid>
                    {/* <Grid item xs={12}>
                      <SelectLanguage languageIndex={languageIndex} setLanguageIndex={setLanguageIndex} />
                    </Grid> */}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={4}>
            <ItemDesignArea ids={ids}
              allItems={{attributes, setAttributes, shapes, setShapes, changeItem}}
              selectingId={selectingId}
              setSelectingId={setSelectingId}
              onChangeAttributeOrder={() => setChangeAttributeOrderAction(true)}
              tabValue={itemDesignTab}
              setTabValue={setItemDesignTab}
            />
          </Grid>
        </Grid>
      </Box>
      <ImageBrowserDialog 
        open={imageBrowserAction.show}
        onAddAttribute={(kind, image) => {
          setImageBrowserAction({ show: false });
          if (imageBrowserAction.type === 'attribute') addNewAttribute(kind, image);
          else if (imageBrowserAction.type === 'shape') addNewShape(kind, image);
        }}
        onClose={() => setImageBrowserAction({ show: false })}
      />
      <DrawToolDialog open={drawToolAction.show} onClose={() => setDrawToolAction({ show: false })}
        onSave={(image: FullSizeImageAddType) => {
          setDrawToolAction({ show: false });
          if (drawToolAction.type === 'attribute') addNewAttribute('image', image);
          else if (drawToolAction.type === 'shape') addNewShape('image', image);
        }}
      />
      <ChangeAttributeOrderDialog open={changeAttributeOrderAction}
        onClose={() => setChangeAttributeOrderAction(false)}
        attributeIds={attributeIds} attributes={attributes}
        setAttributes={setAttributes} 
      />
      <ChangeCardTemplateNotesDialog open={changeCardTemplateNotesAction}
        onClose={() => setChangeCardTemplateNotesAction(false)}
        notes={template.notes}
        setNotes={(value) => setTemplate({...template, notes: value})}
      />
      <SetCardCustomSizeDialog open={sizeCustomMenuOpen}
        onClose={() => setSizeCustomMenuOpen(false)}
        changeCardSize={(size) => changeCardSize(size)}
      />
    </>
  );
}

export default DesignTemplate;
